import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Button from '../ui/Button';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', href: '/services' },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'About', href: '/about' },
    { label: 'Blog', href: '/blog' },
  ];

  return (
    <>
      <m.nav
        initial={{ y: '-100%' }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/50 backdrop-blur-md border-b border-white/10 py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <Link to="/" className="flex items-center group relative">
            <m.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
              className="relative"
            >
              {/* Glow ring that pulses on idle */}
              <m.div
                className="absolute inset-0 rounded-none blur-md bg-[#CCFF00] opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none"
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* The actual logo image */}
              <m.img
                src="/logo.webp"
                alt="HashQ Technologies"
                width={174}
                height={40}
                className="h-8 md:h-10 w-auto relative z-10 transition-all duration-300"
                style={{ filter: 'invert(1) brightness(1)' }}
                decoding="async"
                whileHover={{
                  filter: 'invert(0.8) sepia(1) saturate(10) hue-rotate(30deg) brightness(1.2)',
                  scale: 1.05,
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />

              {/* Text fallback if image fails */}
              <div className="hidden text-2xl font-black tracking-tighter items-center gap-0">
                {['H','A','S','H','Q'].map((char, i) => (
                  <span key={i} className="text-white group-hover:text-[#CCFF00] transition-colors duration-300">{char}</span>
                ))}
                <span className="w-[5px] h-[5px] bg-[#CCFF00] ml-1 mt-1" />
              </div>
            </m.div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link 
                key={link.label} 
                to={link.href}
                className="group relative text-gray-300 hover:text-white transition-colors text-sm font-medium tracking-wide"
              >
                {link.label}
                <span className="absolute -bottom-1 left-1/2 w-0 h-[2px] bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
              </Link>
            ))}
          </div>

          <div className="hidden md:block">
            <Link to="/contact">
              <Button variant="outline">Contact Us</Button>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open mobile menu"
          >
            <Menu size={28} />
          </button>
        </div>
      </m.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <m.div
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(16px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center"
          >
            <button 
              className="absolute top-6 right-6 text-white"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close mobile menu"
            >
              <X size={32} />
            </button>
            <div className="flex flex-col gap-8 text-center text-3xl font-bold tracking-tight">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors">Home</Link>
              {navLinks.map((link) => (
                <Link 
                  key={link.label} 
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-400 hover:text-white hover:text-glow transition-all"
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="mt-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Contact Us
              </Link>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
